import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, Lightbulb, Award, Users, TrendingUp, ArrowRight } from 'lucide-react';
import Seo from '../components/Seo';

const AboutPage = () => {
  const values = [
    {
      icon: <Target className="w-12 h-12 text-cyan-300" />,
      title: 'Patient-Centered',
      description: 'Every decision we make starts with improving patient outcomes and experiences.',
    },
    {
      icon: <Lightbulb className="w-12 h-12 text-cyan-300" />,
      title: 'Innovation',
      description: 'We continuously push the boundaries of what AI can do for healthcare operations.',
    },
    {
      icon: <Users className="w-12 h-12 text-cyan-300" />,
      title: 'Collaboration',
      description: 'We work closely with healthcare professionals to understand and solve real challenges.',
    },
    {
      icon: <Award className="w-12 h-12 text-cyan-300" />,
      title: 'Excellence',
      description: 'We are committed to delivering the highest quality solutions and support.',
    },
  ];

  const milestones = [
    { 
      year: '2021-2022', 
      title: 'Foundational Research', 
      bullet1: 'Began as a Purdue/IU-led research collaboration on AI-driven census prediction and nurse staffing optimization.',
      bullet2: 'Developed core algorithms for forecasting and dynamic nurse allocation.' 
    },
    { 
      year: '2023', 
      title: 'First Pilot at IU Health', 
      bullet1: 'Deployed DeltaOps regional float nurse deployment engine at IU Health. One of the first few regional float program in the country, supported by AI analytics.',
      bullet2: 'Proven outcomes: reduced understaffing by 17%, lowered reliance on travel nurses, improved workforce efficiency.' 
    },
    { 
      year: '2024', 
      title: 'Recognition & Momentum', 
      bullet1: 'Won multiple national awards for innovation and applied healthcare AI.',
      bullet2: 'Expanded research into explainability and multi-period decision optimization.',
      bullet3: 'System validated across multiple inpatient units.',
      bullet4: 'Began exploring commercial pathways.'
    },
    { 
      year: '2024-2025', 
      title: 'NSF I-Corps Discovery for Product-Market Fit', 
      bullet1: 'Completed regional and national NSF I-Corps programs.',
      bullet2: 'Conducted 200+ customer discovery interviews across hospitals, IDNs, home health agencies, and post-acute care.',
      bullet3: 'Identified strong demand for operational AI across care transitions workflows.' 
    },
    { year: '2025', 
      title: 'Company Formation & Expansion', 
      bullet1: 'Founded DeltaOps AI, Inc.',
      bullet2: 'Secured two additional pilots with major healthcare partners (Queens Medical Center and UT Southwestern).',
      bullet3: 'Submitted NIH SBIR Phase I for AI-driven workforce management.' 
    },
    {
      year: 'Late 2025',
      title: 'Expansion Beyond Hospitals',
      bullet1: 'Extending platform to support care transitions, home health, and home care operations.',
      bullet2: 'Began shaping the AI operating layer for post-acute care.'
    }
  ];

  const stats = [
    { value: '10+', label: 'Healthcare Partners' },
    { value: '10K+', label: 'Patients Impacted' },
    { value: '$6M+', label: 'Value Created' }
  ];

  return (
    <div className="about-page" data-testid="about-page">
      <Seo
        title="About DeltaOps AI | Mission, Vision, and Journey"
        description="Discover the mission behind DeltaOps AI, our vision for healthcare operations, and the milestones that shaped our coordination platform."
        image="https://images.pexels.com/photos/17486100/pexels-photo-17486100.png"
        imageAlt="AI-driven healthcare operations visualization"
      />
      {/* Hero Section */}
      <section
        className="min-h-screen flex items-center bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-900/20 text-white"
        data-testid="hero-section"
      >
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-4xl space-y-6">
            <p className="text-cyan-300 uppercase tracking-widest text-sm">Operations Intelligence</p>
            <h1 className="text-4xl md:text-5xl font-bold leading-tight" data-testid="page-title">
              About DeltaOps AI
            </h1>
            <p className="text-lg text-slate-200">
              We coordinate the behind-the-scenes complexity of hospitals and home health operations so care teams can focus on patients.
              Our agentic AI transforms fragmented signals into actionable workflows that keep every department and partner aligned.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="btn bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-3 rounded-lg transition hover:-translate-y-0.5"
              >
                Request Operations Review <ArrowRight className="ml-2" />
              </Link>
              <Link
                to="/operations-flow"
                className="btn border border-white/20 px-6 py-3 rounded-lg text-white hover:text-cyan-300"
              >
                Explore the Operations Flow
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-[#020a17]" data-testid="mission-section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-6 text-white">Our Mission</h2>
              <p className="text-xl text-slate-300 leading-relaxed">
                To transform healthcare operations through intelligent AI coordination, enabling hospitals to provide exceptional patient care while supporting their workforce and optimizing resources.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
              <div className="bg-gradient-to-br from-white/5 via-cyan-900/10 to-transparent p-8 rounded-xl border border-white/10">
                <Eye className="w-12 h-12 text-cyan-300 mb-4" />
                <h3 className="text-2xl font-bold mb-3 text-white">Our Vision</h3>
                <p className="text-slate-300">
                  A healthcare system where every hospital operates at peak efficiency, every clinician feels supported, and every patient receives timely, high-quality care.
                </p>
              </div>

              <div className="bg-gradient-to-br from-white/5 via-cyan-900/10 to-transparent p-8 rounded-xl border border-white/10">
                <Target className="w-12 h-12 text-cyan-300 mb-4" />
                <h3 className="text-2xl font-bold mb-3 text-white">Our Approach</h3>
                <p className="text-slate-300">
                  We leverage cutting-edge agentic AI to capture real-world signals that traditional systems miss, turning hidden operational data into actionable insights for immediate impact.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-slate-900 via-slate-900 to-cyan-900 text-white" data-testid="stats-section">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <div className="text-5xl font-bold mb-2">{stat.value}</div>
                <div className="text-cyan-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-slate-900" data-testid="values-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-white">Our Values</h2>
            <p className="text-xl text-slate-300">The principles that guide everything we do</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white/5 p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all text-center border border-white/10"
                data-testid={`value-${index}`}
              >
                <div className="flex justify-center mb-4">{value.icon}</div>
                <h3 className="text-xl font-bold mb-3 text-white">{value.title}</h3>
                <p className="text-slate-300">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Background Section */}
      <section className="py-20 bg-[#020a17]" data-testid="team-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-white">Our Team</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Our expertise is built on the foundation of world-class institutions in healthcare, technology, and research
            </p>
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
              <div className="flex items-center justify-center p-6 bg-white rounded-xl border border-white/10 hover:bg-white transition-all">
                <img 
                  src="/media/logos/team/Purdue_University.png" 
                  alt="Purdue University" 
                  className="w-full h-auto max-h-20 object-contain opacity-80 hover:opacity-100 transition-opacity"
                />
              </div>
              <div className="flex items-center justify-center p-6 bg-white rounded-xl border border-white/10 hover:bg-white transition-all">
                <img 
                  src="/media/logos/team/Penn_Wharton.png" 
                  alt="Wharton School of Business" 
                  className="w-full h-auto max-h-20 object-contain opacity-80 hover:opacity-100 transition-opacity"
                />
              </div>
              <div className="flex items-center justify-center p-6 bg-white rounded-xl border border-white/10 hover:bg-white transition-all">
                <img 
                  src="/media/logos/team/Athenahealth.png" 
                  alt="Athenahealth" 
                  className="w-full h-auto max-h-20 object-contain opacity-80 hover:opacity-100 transition-opacity"
                />
              </div>
              <div className="flex items-center justify-center p-6 bg-white rounded-xl border border-white/10 hover:bg-white transition-all">
                <img 
                  src="/media/logos/team/OSH_Logo.png" 
                  alt="Oak Street Health" 
                  className="w-full h-auto max-h-20 object-contain opacity-80 hover:opacity-100 transition-opacity"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-[#030d1f]" data-testid="timeline-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-white">Our Journey</h2>
            <p className="text-xl text-slate-300">Milestones in transforming healthcare operations</p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 md:left-1/2 top-0 h-full w-px md:w-1 bg-cyan-900/50 md:-translate-x-1/2"></div>
              
              {milestones.map((milestone, index) => (
                <div
                  key={index}
                  className={`relative mb-10 flex flex-col md:mb-12 md:items-center ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                  data-testid={`milestone-${index}`}
                >
                  <div
                    className={`w-full md:w-1/2 pl-12 md:pl-0 text-left ${
                      index % 2 === 0 ? 'md:pr-8 md:text-right' : 'md:pl-8 md:text-left'
                    }`}
                  >
                    <div className="relative z-10 bg-white/5 p-6 rounded-xl shadow-lg border border-white/10">
                      <div className="text-3xl font-bold text-cyan-300 mb-2">{milestone.year}</div>
                      <h3 className="text-xl font-bold mb-2 text-white">{milestone.title}</h3>
                      <p className="text-slate-300">{milestone.bullet1}</p>
                      <p className="text-slate-300">{milestone.bullet2}</p>
                      <p className="text-slate-300">{milestone.bullet3}</p>
                      <p className="text-slate-300">{milestone.bullet4}</p>
                    </div>
                  </div>
                  <div className="absolute left-4 md:left-1/2 top-6 md:top-1/2 w-4 h-4 bg-cyan-400 rounded-full border-4 border-slate-900 md:-translate-x-1/2 md:-translate-y-1/2"></div>
                  <div className="hidden md:block w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-20 bg-slate-900" data-testid="technology-section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6 text-white">Our Technology</h2>
            <p className="text-lg text-slate-300 mb-8 leading-relaxed">
              DeltaOps AI uses advanced <span className="font-semibold text-cyan-300">agentic AI</span> and machine learning to process real-time operational data from multiple sources—messages, shift notes, team communications, and EHR systems.
            </p>
            <p className="text-lg text-slate-300 mb-8 leading-relaxed">
              Our platform continuously learns and adapts, identifying patterns and predicting bottlenecks before they occur. This enables proactive decision-making and seamless coordination across all hospital departments.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <div className="bg-white/5 p-6 rounded-xl shadow-lg border border-white/10">
                <TrendingUp className="w-10 h-10 text-cyan-300 mb-4 mx-auto" />
                <h3 className="text-lg font-bold mb-2 text-white">Predictive Analytics</h3>
                <p className="text-slate-300 text-sm">Forecast patient surges and resource needs with 95% accuracy</p>
              </div>
              <div className="bg-white/5 p-6 rounded-xl shadow-lg border border-white/10">
                <Users className="w-10 h-10 text-cyan-300 mb-4 mx-auto" />
                <h3 className="text-lg font-bold mb-2 text-white">Real-Time Coordination</h3>
                <p className="text-slate-300 text-sm">Connect teams instantly with intelligent communication workflows</p>
              </div>
              <div className="bg-white/5 p-6 rounded-xl shadow-lg border border-white/10">
                <Lightbulb className="w-10 h-10 text-cyan-300 mb-4 mx-auto" />
                <h3 className="text-lg font-bold mb-2 text-white">Continuous Learning</h3>
                <p className="text-slate-300 text-sm">AI that adapts to your unique operational patterns and improves over time</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-cyan-600/20 via-slate-900 to-slate-950 text-white" data-testid="cta-section">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Join Us in Transforming Healthcare</h2>
          <p className="text-xl mb-8 text-cyan-100 max-w-2xl mx-auto">
            Partner with DeltaOps AI to unlock your hospital's full potential.
          </p>
          <a
            href="/contact"
            className="btn bg-white/10 text-white border border-white/30 hover:bg-white/20 text-lg px-8 py-4"
            data-testid="cta-button"
          >
            Get in Touch
          </a>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
